import React from "react";
import { TFunction } from "i18next";
import { toast, ToastOptions } from "react-toastify";
import { CheckboxIcon } from "@aureskonnect/react-ui";
import { uuid } from "uuidv4";
import { useDispatch } from "react-redux";

import { CESAR_LOYALTY } from "@constants/index";
import { buildFidelityPayload } from "../../helpers";
import { AppDispatch } from "../../../../../redux/store";
import {
    setIsLoading,
    setKey,
} from "../../../../../redux/slices/fidelitySlice";

type Params = {
    selectedRows: FidelityRow[];
    oneShop: boolean;
    userID: number;
    franchiseID: string;
    operatorID: string;
    uuidUser: string | null;
    fetchGlobal: FetchGlobal;
    setLimit: React.Dispatch<React.SetStateAction<number>>;
    t: TFunction;
};

const successIcon = <CheckboxIcon height={25} width={25} fill="white" />;

export function useBulkActivateFidelity(params: Params) {
    const {
        selectedRows,
        oneShop,
        userID,
        franchiseID,
        operatorID,
        uuidUser,
        fetchGlobal,
        setLimit,
        t,
    } = params;
    const dispatch = useDispatch<AppDispatch>();

    return React.useCallback(async () => {
        dispatch(setIsLoading(true));
        let cantActive = false;
        let hasError = false;

        const data: Record<string, FidelityPayload> = {};
        const shopIds: string[] = [];

        for (const el of selectedRows) {
            if (el.fidelityChoiceIndex !== CESAR_LOYALTY) {
                setLimit(2);
                cantActive = true;
                continue;
            }

            const index = (el.isArchived as FidelityArchiveEntry[]).findIndex(
                // eslint-disable-next-line no-loop-func
                (shop: FidelityArchiveEntry) =>
                    shop.shopId === `${el.affectedShop}` ||
                    shop.shopId === el.affectedShop
            );
            if (index !== -1) {
                (el.isArchived as FidelityArchiveEntry[])[index].status = false;
            }

            if (!shopIds.includes(el.shopId.toString())) {
                shopIds.push(el.shopId.toString());
            }

            const payload = buildFidelityPayload(el, t);
            payload.isArchived = JSON.stringify(el.isArchived);
            data[el.Id] = payload;
        }

        const body = {
            userId: userID,
            shopId: shopIds,
            franchiseId: franchiseID,
            operatorId: operatorID,
            uuidUser,
            isShop: false,
            data,
        };
        const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity`;

        try {
            const result = (await fetchGlobal(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            })) as { error: boolean };
            if (result.error) hasError = true;
        } catch (e) {
            hasError = true;
            toast.error(t("There's an error"), {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }

        dispatch(setIsLoading(false));

        if (!hasError) {
            toast.success(
                t("The fidelity setting has been successfully unarchive"),
                {
                    position: toast.POSITION.TOP_CENTER,
                    theme: "colored",
                    icon: successIcon,
                } as ToastOptions
            );
        }

        if (cantActive) {
            const message = oneShop
                ? t(
                      "Attention,a shop cannot have more than one fidelity settings"
                  )
                : t(
                      "Attention, one or more loyalty settings could not be successfully unarchived. A franchise cannot have more than one type of loyalty system."
                  );
            toast.warning(message, {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: successIcon,
            } as ToastOptions);
        }

        dispatch(setKey(uuid()));
        // Why: `dispatch` is stable per React-Redux contract. `fetchGlobal` (from
        // useFetchGlobal) and `setLimit` are recreated each parent render — including
        // them would refresh the callback on every render and defeat memoization.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRows, oneShop, userID, franchiseID, operatorID, uuidUser, t]);
}
